import React, { useState, useEffect, useMemo } from 'react';
import {
  AgentRegistry,
  AuditLogger,
  TaskRouter,
  MockRagProvider,
  MockLlmWikiProvider,
  MockBuzzAdapter,
  MockSimulatorRuntime,
  ChannelBindingRegistry,
  TriggerMatcher,
  InMemoryKanbanProvider,
  ScrumMasterWorkflow,
  ChannelAgentRouter,
  ProposalService,
  ProposalExecutor,
  createAgentIdentity,
  type AgentProfile,
  type KnowledgeMode,
  type AgentCapability,
  type Citation,
  type AgentTaskRequest,
  type AuditRecord,
  type ChannelAgentBinding,
  type KanbanProposal,
  type KanbanTask,
  type ExecuteProposalRequest,
  type BindingId,
  type ChannelId,
  type AgentId,
  type TenantId,
  type EntityId,
} from 'mesh-microkernel';
import { KanbanProposalCard, getKanbanTasks } from '@herx-module/kanban';
import { globalWorkspaceDirectory } from '../mesh/governance/workspace-directory.js';

interface AgentDirectoryPaneProps {
  currentRoom: string;
  nodeId: string;
  tenantId?: string;
  height?: number;
}

export const AgentDirectoryPane: React.FC<AgentDirectoryPaneProps> = ({
  currentRoom,
  nodeId,
  tenantId = nodeId,
  height = 450,
}) => {
  const [activeTab, setActiveTab] = useState<'catalog' | 'bindings' | 'kanban'>('bindings');

  // Core Engines & Registries
  const {
    registry,
    auditLogger,
    router,
    bindingRegistry,
    channelRouter,
    kanbanProvider,
    proposalService,
    proposalExecutor,
  } = useMemo(() => {
    const reg = new AgentRegistry();
    const logger = new AuditLogger();
    const rag = new MockRagProvider();
    const sim = new MockSimulatorRuntime();

    // Register Agents
    const agentsToRegister = [
      { id: 'agent-scrum-master', name: 'ACME Scrum Master Agent', providerId: 'scrum-master-workflow', caps: ['read_sprint', 'read_kanban', 'summarize_standup', 'create_task_draft'] },
      { id: 'agent-faq-rag', name: 'ACME FAQ Support Agent (RAG)', providerId: 'mock-rag-provider', caps: ['read_knowledge', 'summarize'] },
      { id: 'agent-wiki-domain', name: 'Domain Policy Agent (LLMWiki)', providerId: 'mock-llmwiki-provider', caps: ['read_knowledge', 'draft_content'] },
      { id: 'agent-buzz-acp', name: 'Buzz ACP Nostr Agent', providerId: 'mock-buzz-acp-adapter', caps: ['read_knowledge', 'write_internal_message'] },
    ];

    for (const a of agentsToRegister) {
      const identity = createAgentIdentity({
        id: a.id as any,
        ownerId: nodeId as any,
        tenantId: tenantId as any,
      });
      reg.registerAgent(identity, {
        id: a.id,
        identityId: identity.id,
        name: a.name,
        providerId: a.providerId,
        capabilities: a.caps as any,
        version: '1.0.0',
      });
    }

    const bReg = new ChannelBindingRegistry();
    const matcher = new TriggerMatcher();
    const kanban = new InMemoryKanbanProvider(tenantId as TenantId);
    const scrum = new ScrumMasterWorkflow(kanban);

    // Initial #scrum binding
    bReg.bindAgent({
      bindingId: 'binding-scrum-01' as BindingId,
      channelId: 'scrum' as ChannelId,
      agentId: 'agent-scrum-master' as AgentId,
      tenantId: tenantId as TenantId,
      role: 'scrum_master',
      status: 'active',
      triggerPolicy: {
        respondWhenMentioned: true,
        respondToCommands: ['/standup', '/blocker', '/sprint'],
        respondToKeywords: ['standup', 'blocked', 'help', 'sprint', 'kanban'],
      },
      knowledgePolicy: { mode: 'hybrid', wikiScopes: ['scrum-guide'], ragScopes: ['current-sprint'] },
      capabilityPolicy: { allowedCapabilities: ['read_sprint', 'create_task_draft'], requiresApprovalFor: ['create_task'] },
      createdBy: nodeId as EntityId,
      updatedBy: nodeId as EntityId,
      version: 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });

    const cRouter = new ChannelAgentRouter(bReg, matcher, scrum, rag);
    const propSvc = new ProposalService();
    const propExec = new ProposalExecutor(bReg, propSvc, kanban, logger);

    const taskRouter = new TaskRouter(reg, logger, rag, sim);

    return {
      registry: reg,
      auditLogger: logger,
      router: taskRouter,
      bindingRegistry: bReg,
      channelRouter: cRouter,
      kanbanProvider: kanban,
      proposalService: propSvc,
      proposalExecutor: propExec,
    };
  }, [nodeId, tenantId]);

  const [agentsList, setAgentsList] = useState<AgentProfile[]>([]);
  const [bindingsList, setBindingsList] = useState<ChannelAgentBinding[]>([]);
  const [proposalsList, setProposalsList] = useState<KanbanProposal[]>([]);
  const [kanbanTasks, setKanbanTasks] = useState<KanbanTask[]>([]);
  const [auditStream, setAuditStream] = useState<AuditRecord[]>([]);

  const [selectedAgentId, setSelectedAgentId] = useState<string>('agent-scrum-master');
  const [knowledgeMode, setKnowledgeMode] = useState<KnowledgeMode>('hybrid');
  const [promptInput, setPromptInput] = useState<string>('/blocker API contract dependency missing');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTaskEvents, setActiveTaskEvents] = useState<string[]>([]);

  const refreshData = async () => {
    setAgentsList(registry.listAgents(tenantId));
    setBindingsList(bindingRegistry.getChannelBindings(currentRoom as ChannelId, tenantId as TenantId));
    setProposalsList(proposalService.listPendingProposals(currentRoom as ChannelId, tenantId as TenantId));
    const liveTasks = Array.from(getKanbanTasks(currentRoom).values()).map((t) => ({
      id: t.id as any,
      tenantId: tenantId as any,
      boardId: `board-${currentRoom}`,
      title: t.title,
      description: t.description || '',
      status: t.status,
      assigneeId: t.assignee,
      comments: [],
      linkedTaskIds: [],
      createdAt: t.createdAt,
      updatedAt: t.updatedAt,
    }));
    setKanbanTasks(liveTasks);
    setAuditStream(auditLogger.getTenantAuditTrail(tenantId));
  };

  useEffect(() => {
    refreshData();
  }, [currentRoom, tenantId]);

  const handleDispatchTask = async () => {
    if (!promptInput.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setActiveTaskEvents([]);

    try {
      // First try Channel Agent Router dispatch
      const channelRes = await channelRouter.dispatchChannelMessage({
        tenantId: tenantId as TenantId,
        channelId: currentRoom as ChannelId,
        senderId: nodeId as EntityId,
        messageText: promptInput,
      });

      if (channelRes.matched) {
        setActiveTaskEvents((prev) => [...prev, channelRes.responseText || 'Channel Agent matched prompt']);
        if (channelRes.proposals.length > 0) {
          for (const prop of channelRes.proposals) {
            proposalService.saveProposal(prop);
            setActiveTaskEvents((prev) => [...prev, `💡 PROPOSAL CREATED: ${prop.reason} (${prop.proposalId})`]);
          }
        }
      } else {
        // Direct TaskRouter execution fallback
        const taskId = `task-${Date.now()}`;
        const eventStream = await router.submitTask({
          taskId,
          agentId: selectedAgentId,
          scope: { tenantId, meshId: 'mesh-local', roomId: currentRoom, requesterId: nodeId },
          prompt: promptInput,
          requestedCapabilities: ['read_knowledge'],
          knowledgeMode,
        });

        for await (const ev of eventStream) {
          if (ev.type === 'progress' && ev.delta) setActiveTaskEvents((prev) => [...prev, ev.delta!]);
          else if (ev.type === 'result' && ev.result) setActiveTaskEvents((prev) => [...prev, `✅ TASK RESULT: ${ev.result}`]);
        }
      }
    } catch (err: any) {
      setActiveTaskEvents((prev) => [...prev, `❌ ERROR: ${err.message}`]);
    } finally {
      setIsSubmitting(false);
      refreshData();
    }
  };

  const handleApproveProposal = async (request: ExecuteProposalRequest) => {
    await proposalExecutor.executeProposal(request);
    await refreshData();
  };

  const handleRejectProposal = (proposalId: string) => {
    proposalService.updateProposalStatus(proposalId as any, 'rejected');
    refreshData();
  };

  const handleToggleBindingStatus = (binding: ChannelAgentBinding) => {
    const nextStatus = binding.status === 'active' ? 'paused' : 'active';
    bindingRegistry.updateBindingStatus(binding.bindingId, nextStatus, nodeId as EntityId);
    refreshData();
  };

  return (
    <div
      id="agent-directory-pane-root"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: `${height}px`,
        backgroundColor: '#090d16',
        color: '#f8fafc',
        borderBottom: '1px solid var(--theme-border, #1e293b)',
        overflow: 'hidden',
      }}
    >
      {/* Header Bar */}
      <div
        style={{
          height: '38px',
          backgroundColor: '#0f172a',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#60a5fa' }}>🤖 Channel & Mesh Agent Control Pane</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setActiveTab('bindings')}
              style={{
                padding: '3px 8px',
                fontSize: '11px',
                fontWeight: 700,
                borderRadius: '4px',
                border: 'none',
                background: activeTab === 'bindings' ? '#3b82f6' : 'transparent',
                color: activeTab === 'bindings' ? '#fff' : '#94a3b8',
                cursor: 'pointer',
              }}
            >
              Channel Bindings ({bindingsList.length})
            </button>
            <button
              onClick={() => setActiveTab('kanban')}
              style={{
                padding: '3px 8px',
                fontSize: '11px',
                fontWeight: 700,
                borderRadius: '4px',
                border: 'none',
                background: activeTab === 'kanban' ? '#3b82f6' : 'transparent',
                color: activeTab === 'kanban' ? '#fff' : '#94a3b8',
                cursor: 'pointer',
              }}
            >
              Proposals & Kanban ({proposalsList.length} Pending)
            </button>
            <button
              onClick={() => setActiveTab('catalog')}
              style={{
                padding: '3px 8px',
                fontSize: '11px',
                fontWeight: 700,
                borderRadius: '4px',
                border: 'none',
                background: activeTab === 'catalog' ? '#3b82f6' : 'transparent',
                color: activeTab === 'catalog' ? '#fff' : '#94a3b8',
                cursor: 'pointer',
              }}
            >
              Agent Catalog ({agentsList.length})
            </button>
          </div>
        </div>

        <div style={{ fontSize: '11px', color: '#64748b' }}>Channel: #{currentRoom}</div>
      </div>

      {/* Content Grid */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Column */}
        <div style={{ width: '45%', borderRight: '1px solid #1e293b', padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto' }}>
          {activeTab === 'bindings' && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                Active Channel Agent Bindings
              </div>
              {bindingsList.map((b) => (
                <div key={b.bindingId} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#60a5fa' }}>
                      {b.agentId} ({b.role})
                    </span>
                    <button
                      onClick={() => handleToggleBindingStatus(b)}
                      style={{
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontWeight: 700,
                        borderRadius: '4px',
                        border: 'none',
                        background: b.status === 'active' ? '#ef4444' : '#10b981',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {b.status === 'active' ? 'Pause' : 'Resume'}
                    </button>
                  </div>
                  <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                    Triggers: {b.triggerPolicy.respondToCommands.join(', ')} | Status: {b.status.toUpperCase()} (v{b.version})
                  </div>
                </div>
              ))}
            </>
          )}

          {activeTab === 'catalog' && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                Global Mesh Agent Catalog
              </div>
              {agentsList.map((ag) => (
                <div key={ag.id} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#e2e8f0' }}>{ag.name}</div>
                  <div style={{ fontSize: '10px', color: '#64748b' }}>Provider: {ag.providerId}</div>
                </div>
              ))}

              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginTop: '12px' }}>
                Compute Resources ({globalWorkspaceDirectory.getComputeNodes(tenantId).length})
              </div>
              {globalWorkspaceDirectory.getComputeNodes(tenantId).length === 0 ? (
                <div style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic' }}>
                  No compute-only nodes configured in this workspace.
                </div>
              ) : (
                globalWorkspaceDirectory.getComputeNodes(tenantId).map((node) => (
                  <div key={node.principalId} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8' }}>🖥️ {node.displayName}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Node ID: {node.principalId} | Status: {node.status.toUpperCase()}</div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'kanban' && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                Sprint 42 Kanban Tasks ({kanbanTasks.length})
              </div>
              {kanbanTasks.map((t) => (
                <div key={t.id} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: t.status === 'blocked' ? '#ef4444' : '#34d399' }}>
                    [{t.status.toUpperCase()}] {t.title}
                  </div>
                  <div style={{ fontSize: '10px', color: '#64748b' }}>Assignee: {t.assigneeId || 'Unassigned'}</div>
                </div>
              ))}
            </>
          )}

          {/* Interactive Prompt Dispatch Form */}
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              Test Channel / Agent Prompt
            </div>
            <textarea
              value={promptInput}
              onChange={(e) => setPromptInput(e.target.value)}
              rows={2}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#fff', padding: '6px', fontSize: '11px' }}
            />
            <button
              onClick={handleDispatchTask}
              disabled={isSubmitting}
              style={{ padding: '6px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer' }}
            >
              {isSubmitting ? 'Processing...' : '🚀 Submit Message / Command'}
            </button>
          </div>
        </div>

        {/* Right Column: Proposals Feed & Output Logs */}
        <div style={{ flex: 1, padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
            Pending Kanban Proposals ({proposalsList.length})
          </div>

          {proposalsList.length === 0 ? (
            <div style={{ fontSize: '11px', color: '#475569', fontStyle: 'italic' }}>
              No pending Kanban proposals for #{currentRoom}. Post `/blocker [issue]` to generate a proposal.
            </div>
          ) : (
            proposalsList.map((prop) => (
              <KanbanProposalCard
                key={prop.proposalId}
                proposal={prop}
                onApprove={handleApproveProposal}
                onReject={handleRejectProposal}
              />
            ))
          )}

          {/* Execution Log */}
          <div style={{ flex: 1, background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', overflowY: 'auto' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
              Execution & Event Output Stream
            </div>
            {activeTaskEvents.map((ev, idx) => (
              <div key={idx} style={{ fontSize: '10px', color: '#cbd5e1', fontFamily: 'monospace', marginBottom: '2px' }}>
                {ev}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
